import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { logoutUser, switchContext } from './authSlice'

interface SurveyState {
    pagination: {
        page: number
        limit: number
        totalSurveys: number
        totalPages: number
    }
    activeSurvey: {
        id: string | null
        name: string | null
        status: 'draft' | 'published' | 'archived' | null
    }
    filters: {
        status: ('draft' | 'published' | 'archived')[]
        search: string
    }
    analytics: {
        tab:
            | 'analytics'
            | 'responses'
            | 'ai-summary'
            | 'advanced-analytics'
            | 'nps'
            | 'customer-nps-segment'
    }
    liveSurveyCount: number
    createModal: 'create' | 'preview' | null
    channel: 'pps' | 'web' | 'on-site' | 'whatsapp'
}

const initialState: SurveyState = {
    pagination: {
        page: 1,
        limit: 5,
        totalSurveys: 0,
        totalPages: 0,
    },
    activeSurvey: {
        id: null,
        name: null,
        status: null,
    },
    filters: {
        status: [],
        search: '',
    },
    analytics: {
        tab: 'analytics',
    },
    liveSurveyCount: 0,
    createModal: null,
    channel: 'pps',
}

const surveySlice = createSlice({
    name: 'survey',
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.pagination.limit = action.payload
        },
        setTotalSurveys: (state, action: PayloadAction<number>) => {
            state.pagination.totalSurveys = action.payload
            state.pagination.totalPages = Math.ceil(
                action.payload / state.pagination.limit
            )
        },
        setActiveSurvey: (
            state,
            action: PayloadAction<{
                id: string | null
                name: string | null
                status: 'draft' | 'published' | 'archived' | null
            }>
        ) => {
            state.activeSurvey = action.payload
        },
        setStatusFilter: (
            state,
            action: PayloadAction<('draft' | 'published' | 'archived')[]>
        ) => {
            state.filters.status = action.payload
        },
        setSearchFilter: (state, action: PayloadAction<string>) => {
            state.filters.search = action.payload
        },
        resetFilters: (state) => {
            state.filters = initialState.filters
        },
        setLiveSurveyCount: (state, action: PayloadAction<number>) => {
            state.liveSurveyCount = action.payload
        },
        setAnalyticsTab: (
            state,
            action: PayloadAction<
                | 'analytics'
                | 'responses'
                | 'ai-summary'
                | 'advanced-analytics'
                | 'nps'
                | 'customer-nps-segment'
            >
        ) => {
            state.analytics.tab = action.payload
        },
        setCreateModal: (
            state,
            action: PayloadAction<'create' | 'preview' | null>
        ) => {
            state.createModal = action.payload
        },
        setChannel: (
            state,
            action: PayloadAction<'pps' | 'web' | 'on-site' | 'whatsapp'>
        ) => {
            state.channel = action.payload
        },
        resetSurveyState: () => initialState,
    },
    extraReducers: (builder) => {
        // Reset survey state when user logs out or switches workspace
        builder
            .addCase(logoutUser.fulfilled, () => initialState)
            .addCase(logoutUser.rejected, () => initialState)
            .addCase(switchContext.fulfilled, () => initialState)
    },
})

export const {
    setPage,
    setLimit,
    setTotalSurveys,
    setActiveSurvey,
    setStatusFilter,
    setSearchFilter,
    resetFilters,
    setLiveSurveyCount,
    setAnalyticsTab,
    setCreateModal,
    setChannel,
    resetSurveyState,
} = surveySlice.actions

export default surveySlice.reducer
